import { Menu, Transition } from "@headlessui/react";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import { noop } from "lodash";
import React, {
  Fragment,
  MouseEvent,
  MouseEventHandler,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { usePopper } from "react-popper";
import { Link } from "react-router-dom";
import classNames from "../../utils/classNames";

export interface MenuItem {
  text: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  to?: string;
  disabled?: boolean;
}

interface ContextValue {
  showDropdownMenu: (items: MenuItem[], event: MouseEvent) => void;
}

const DropdownMenuContext = React.createContext<ContextValue | undefined>(
  undefined
);

const DropdownMenuProvider = ({ children }: PropsWithChildren<{}>) => {
  const state = useRef<"closed" | "opening" | "open" | "closing">("closed");
  const openButtonRef = useRef<HTMLButtonElement | null>(null);
  const [currentItems, setCurrentItems] = useState<MenuItem[]>([]);
  const [eventTarget, setEventTarget] = useState<Element | null>(null);
  const [menuRef, setMenuRef] = useState<HTMLElement | null>(null);
  const [internalRef, setInternalRef] = useState<HTMLElement | null>(null);
  const { styles, attributes } = usePopper(eventTarget, menuRef, {
    modifiers: [
      {
        name: "computeStyles",
        options: {
          adaptive: false
        }
      },
      {
        name: "offset",
        options: {
          offset: [0, 8]
        }
      },
      {
        name: "hide"
      }
    ]
  });

  const showDropdownMenu: ContextValue["showDropdownMenu"] = (items, event) => {
    if (!openButtonRef.current) return;

    if (
      state.current === "closed" ||
      (state.current === "open" && event.currentTarget !== eventTarget)
    ) {
      setCurrentItems(items);
      setEventTarget(event.currentTarget);
      openButtonRef.current.click();
      if (state.current === "closed") {
        state.current = "opening";
      }
      return;
    }

    if (state.current === "open" && event.currentTarget === eventTarget) {
      state.current = "closed";
      return;
    }
  };

  useEffect(() => {
    if (!internalRef) {
      setEventTarget(null);
      setMenuRef(null);

      state.current = "closed";

      return;
    }

    state.current = "open";
    setMenuRef(internalRef.parentElement);
  }, [internalRef]);

  return (
    <DropdownMenuContext.Provider
      value={{
        showDropdownMenu
      }}
    >
      {children}
      <Menu as="div">
        {({ open }) => (
          <>
            <Menu.Button
              ref={openButtonRef}
              className="hidden"
              as="button"
            ></Menu.Button>
            <Transition
              show={open}
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                static
                className="z-40 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 popper"
                style={styles.popper}
                {...attributes.popper}
              >
                <div ref={setInternalRef} className="py-1">
                  {currentItems.map((item, index) => (
                    <Menu.Item key={index} disabled={item.disabled}>
                      {({ active }) => {
                        const itemClassNames = classNames(
                          active
                            ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                            : "text-gray-700 dark:bg-gray-800 dark:text-white",
                          item.disabled && "opacity-75 cursor-default",
                          "w-full flex justify-between px-4 py-2 text-sm truncate"
                        );

                        if (item.to) {
                          return (
                            <Link
                              to={item.to}
                              onClick={item.onClick || noop}
                              className={itemClassNames}
                            >
                              {item.text}
                            </Link>
                          );
                        }

                        return (
                          <button
                            type="button"
                            onClick={item.onClick || noop}
                            className={itemClassNames}
                          >
                            {item.text}
                          </button>
                        );
                      }}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </DropdownMenuContext.Provider>
  );
};

const DottedMenu = ({
  disabled = false,
  menuItems,
  text,
  className = ""
}: {
  disabled?: boolean;
  menuItems: MenuItem[];
  text: string;
  className?: string;
}) => {
  const { showDropdownMenu } = useDropdownMenu();

  return (
    <button
      onClick={(evt) => {
        evt?.stopPropagation();
        showDropdownMenu(menuItems, evt);
      }}
      disabled={disabled}
      className={classNames(
        "p-2 rounded-full bg-white flex items-center text-gray-400 hover:text-gray-600 focus:outline-none disabled:hover:bg-transparent disabled:cursor-default disabled:hover:text-gray-400 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:hover:bg-gray-700 dark:disabled:hover:bg-gray-800",
        className
      )}
    >
      <span className="sr-only">{text}</span>
      <DotsVerticalIcon className="h-5 w-5" aria-hidden="true" />
    </button>
  );
};

const useDropdownMenu = (): ContextValue => {
  const context = useContext(DropdownMenuContext);
  if (context === undefined) {
    throw new Error(
      "useDropdownMenuProvider must be used within an DropdownMenuProvider"
    );
  }
  return context;
};

export { useDropdownMenu, DottedMenu, DropdownMenuProvider };
